import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import Toolbar from "@mui/material/Toolbar";
import CollaborationCompanionToolbar from "./CollaborationCompanionToolbar";
import { CredsContext, HelpModeContext, LogContext } from "./Contexts";
import Grid from "@mui/material/Grid";
import Tools from "./JNSTools";
import GlobalData from "./GlobalData";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import CustomAlert from "./CustomAlert";
import Popover from "@mui/material/Popover";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  INSTANCE_OADA_SETTINGS,
  PROJECT_OADA_SETTINGS,
  USER_OADA_SETTINGS,
} from "./GlobalSettingsList";
import LinearProgress from "@mui/material/LinearProgress";
import { HELP_TEXT } from "./HelpText";

function OAAddOnMain() {
  /**************************************************
   * Variables, states, references
   **************************************************/
  const [visibleScreen, setVisibleScreen] = useState();
  const [dirty, isDirty] = useState(false);
  const [wait, setWait] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [product, setProduct] = useState("oacc");
  const [helpMode, setHelpMode] = useState(
    sessionStorage.getItem("helpmode")
      ? JSON.parse(sessionStorage.getItem("helpmode"))
      : false,
  );
  const [helpTextVisible, setHelpTextVisible] = useState(false);
  const listenerAdded = useRef(false);
  const settings = useRef();
  const helpTextPosition = useRef({ left: 0, top: 0 });
  const helpText = useRef("");
  const log = useRef("");
  const waitMessage = useRef();
  /*const adminCredsRef = useRef(
    JSON.parse(localStorage.getItem("adminCreds")) || {
      instanceType: "Sandbox",
      company: "top step sandbox",
      username: "ccadmin",
      //password: "0ADAoada",
      password: "0ACCoacc",
      deletionType: "Simple",
      userType: "Admin",
    },
  );*/
  const loadedCreds = localStorage.getItem("instanceInfo");
  let parsedCreds;
  if (loadedCreds && loadedCreds.indexOf("company") == -1) {
    parsedCreds = JSON.parse(Tools.decrypt(loadedCreds));
  } else if (loadedCreds) {
    parsedCreds = JSON.parse(loadedCreds);
  }
  const credsRef = useRef(
    parsedCreds || {
      instanceType: "Sandbox",
      orig_company: "",
      orig_username: "",
      company: "",
      username: "",
      password: "",
      deletionType: "Simple",
      userType: "Admin",
    },
  );
  /**************************************************
   * Functions
   **************************************************/

  const handleWait = (value, message) => {
    waitMessage.current = message || null;
    setWait(value);
  };

  const handleCloseHelpText = () => {
    setHelpTextVisible(false);
  };

  function setHelpModeState(value) {
    sessionStorage.setItem("helpmode", JSON.stringify(value));
    setHelpMode(value);
  }

  const displayVisibleScreen = (screen) => {
    setVisibleScreen(screen);
  };

  const selectProduct = (switchSetting) => {
    if (switchSetting) {
      setProduct("oada");
    } else {
      setProduct("oacc");
    }
  };

  const handleConfirmationClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenConfirmation(false);
  };

  async function refreshProjectObject() {
    const storedId = sessionStorage.getItem("refreshProjectId");
    if (storedId) {
      const authString = Tools.getXmlBodyAuthString(credsRef.current);
      const urlString = Tools.getInstanceUrl(credsRef.current);
      const requestString =
        authString +
        '<Read type="Project" method="equal to" limit="1" enable_custom="1"><Project><id>' +
        storedId +
        "</id></Project></Read></request>";
      await Tools.fetchPost(requestString, urlString, "Project").then(
        (projRecRaw) => {
          if (projRecRaw.xml.length > 0) {
            const projRec = Tools.xmlToArray(projRecRaw.xml);
            sessionStorage.setItem("projectObject", JSON.stringify(projRec[0]));
          }
        },
      );
    }
  }

  async function retrieveOADASettings() {
    let associatedClient = 0;
    const adminCredsString = localStorage.getItem("adminCreds");
    let projectObject = sessionStorage.getItem("projectObject");
    if (!projectObject) {
      projectObject = { id: "-1" };
    }
    const adminCreds = JSON.parse(Tools.decrypt(adminCredsString));
    const authString = Tools.getXmlBodyAuthString(adminCreds);
    const urlString = Tools.getInstanceUrl(adminCreds);

    const credsData = new Promise((resolve) => {
      const globalData = new GlobalData(adminCreds);
      globalData.populateTable("User", true).then((userData) => {
        if (userData) {
          const userRec = userData.find(
            (elem) =>
              elem.nickname.toLowerCase() ==
              credsRef.current.username.toLowerCase(),
          );

          // userRec will be undefined if the company is ok but the username is not
          if (userRec) {
            const requestString =
              authString +
              '<Read type="Preference" method="equal to" limit="1000"><Preference><group_name>oadaSettings</group_name><user>-1</user></Preference></Read></request>';
            Tools.fetchPost(requestString, urlString, "Preference").then(
              (instanceSettings) => {
                if (instanceSettings.authStatus != "401") {
                  const instancePrefSettings = Tools.xmlToArray(
                    instanceSettings.xml,
                  );
                  let settingsObject = {};

                  instancePrefSettings.forEach((pref) => {
                    //if (INSTANCE_OADA_SETTINGS.indexOf(pref.name) >= 0) {
                    settingsObject = {
                      ...settingsObject,
                      [pref.name]: pref.setting,
                    };
                  });

                  const userRequestString =
                    authString +
                    '<Read type="Preference" method="equal to" limit="1000"><Preference><group_name>oadaSettings</group_name><user>' +
                    credsRef.current.id +
                    "</user></Preference></Read></request>";
                  Tools.fetchPost(
                    userRequestString,
                    urlString,
                    "Preference",
                  ).then((userSettings) => {
                    const userPrefSettings = Tools.xmlToArray(userSettings.xml);

                    userPrefSettings.forEach((pref) => {
                      settingsObject = {
                        ...settingsObject,
                        [pref.name]: pref.setting,
                      };
                    });

                    const projRequestString =
                      authString +
                      '<Read type="Preference" method="equal to" limit="1000"><Preference><group_name>oadaSettings</group_name><user>' +
                      projectObject.id +
                      "</user></Preference></Read></request>";
                    Tools.fetchPost(
                      projRequestString,
                      urlString,
                      "Preference",
                    ).then((projSettings) => {
                      const projPrefSettings = Tools.xmlToArray(
                        projSettings.xml,
                      );

                      projPrefSettings.forEach((pref) => {
                        settingsObject = {
                          ...settingsObject,
                          [pref.name]: pref.setting,
                        };
                      });

                      settings.current = settingsObject;
                      localStorage.setItem(
                        "oadaSettings",
                        JSON.stringify(settings.current),
                      );
                    });
                  });
                } else {
                  setOpenConfirmation(true);
                  handleWait(false);
                }
              },
            );

            const profileAttachmentRequest =
              authString +
              '<Read type="ResourceAttachment" method="equal to" limit="1"><ResourceAttachment><userid>' +
              credsRef.current.id +
              "</userid><type>AVATAR</type></ResourceAttachment></Read></request>";

            Tools.fetchPost(
              profileAttachmentRequest,
              urlString,
              "ResourceAttachment",
            ).then((rawProfileAttachmentRecord) => {
              if (rawProfileAttachmentRecord.authStatus != "401") {
                if (rawProfileAttachmentRecord.xml.length > 0) {
                  const profileAttachmentArray = Tools.xmlToArray(
                    rawProfileAttachmentRecord.xml,
                  );
                  const profilePicRequest =
                    authString +
                    '<Read type="Attachment" method="equal to" limit="1"><Attachment><id>' +
                    profileAttachmentArray[0].attachment_id +
                    "</id></Attachment></Read></request>";

                  Tools.fetchPost(
                    profilePicRequest,
                    urlString,
                    "Attachment",
                  ).then((rawProfilePicRecord) => {
                    if (rawProfilePicRecord.xml.length > 0) {
                      const profilePicArray = Tools.xmlToArray(
                        rawProfilePicRecord.xml,
                      );
                      const pic = profilePicArray[0].base64_data;
                      settings.current = {
                        ...settings.current,
                        profilePic: pic,
                      };

                      localStorage.setItem(
                        "oadaSettings",
                        JSON.stringify(settings.current),
                      );
                    }
                  });
                }
              } else {
                setOpenConfirmation(true);
                handleWait(false);
              }
            });

            associatedClient = userRec.client || 0;

            credsRef.current = {
              ...credsRef.current,
              client: associatedClient,
              name: userRec.name,
            };

            //const cordToolsObject = new CordTools();
            //cordToolsObject.addUser(credsRef.current);

            /*if (credsRef.current.company && credsRef.current.instanceType) {
              Tools.dbRead(credsRef.current, "collabCenterCustomers").then(
                (dbObj) => {
                  const adminCreds = {
                    instanceType: dbObj.instanceType,
                    company: dbObj.company,
                    username: dbObj.username,
                    password: dbObj.password,
                    deletionType: "Simple",
                    userType: "Admin",
                    id: dbObj.userid,
                  };
                  localStorage.setItem(
                    "adminCreds",
                    Tools.encrypt(JSON.stringify(adminCreds)),
                  );

                  //console.log(JSON.stringify(dbObj));
                },
              );
            }*/
            setCreds(credsRef.current);
            resolve(credsRef.current);
          } else {
            setOpenConfirmation(true);
            handleWait(false);
          }
        } else {
          setOpenConfirmation(true);
          handleWait(false);
        }
      });
    });

    return await credsData;
  }

  const setCreds = (value) => {
    if (value) {
      credsRef.current = value;
    }

    credsRef.current.username = credsRef.current.orig_username.toLowerCase();

    credsRef.current.company = credsRef.current.orig_company.toLowerCase();

    //const globalData = new GlobalData(credsRef.current);

    if (credsRef.current.role) {
      if (credsRef.current.role == "Guest - User") {
        credsRef.current.userType = "generic";
      } else {
        credsRef.current.userType = "regular";
      }
    }

    //localStorage.removeItem("instanceInfo");
    /*
    if (credsRef.current.username) {
      globalData.populateTable("User").then((users) => {
        if (users) {
          const userRec = users.find(
            (usersElem) => usersElem.nickname == credsRef.current.username,
          );
          if (userRec) {
            credsRef.current.id = userRec.id;
            credsRef.current.role = userRec.role;
            localStorage.setItem(
              "instanceInfo",
              JSON.stringify(credsRef.current),
            );
          }
        }
      });
    }*/
    isDirty(!dirty);
  };

  const addLog = (newLog, color) => {
    if (!color) {
      color = "black";
    }
    log.current += "<p style={color:" + color + "}>" + newLog + "</p>";
  };

  const clearLog = () => (log.current = "");

  /*const setSettings = (newSettings) => {
    if (typeof newSettings == "string") {
      localStorage.setItem("oadaSettings", newSettings);
      settings.current = JSON.parse(newSettings);
    } else {
      localStorage.setItem("oadaSettings", JSON.stringify(newSettings));
      settings.current = newSettings;
    }

    const customerNames = JSON.parse(
      localStorage.getItem(credsRef.current.company + "|Customer"),
    );
    if (customerNames && customerNames.length > 0) {
      const foundCust = customerNames.find(
        (elem) => elem.name == newSettings.associatedClient,
      );
      if (foundCust) {
        if (newSettings.associatedClient != credsRef.current.client) {
          const authString = Tools.getXmlBodyAuthString(credsRef.current);
          const urlString = Tools.getInstanceUrl(credsRef.current);
          const userInfo = JSON.parse(
            localStorage.getItem(credsRef.current.company + "|User"),
          );
          const userId = userInfo.find(
            (elem) => elem.nickname == credsRef.current.username,
          ).id;
          const modUserString =
            authString +
            '<Modify type="User" enable_custom="1"><User><id>' +
            +userId +
            "</id><oada_user_client__c>" +
            foundCust.id +
            "</oada_user_client__c></User></Modify></request>";
          Tools.fetchPost(modUserString, urlString);
          setCreds({ ...credsRef.current, client: foundCust.id });
        }
      }
    }
    Tools.writeSettings(credsRef.current);

    isDirty(!dirty);
  };
*/
  const handleClickAnywhere = (event) => {
    const hMode = sessionStorage.getItem("helpmode")
      ? JSON.parse(sessionStorage.getItem("helpmode"))
      : false;
    if (hMode) {
      let currentElement = event.target;
      let brakes = 0;
      while (
        currentElement.id != "topLevelComponent" &&
        currentElement.id.substring(0, 2) != "__" &&
        currentElement.parentElement &&
        brakes < 100
      ) {
        currentElement = currentElement.parentElement;
        brakes++;
      }

      if (!currentElement.id && event.target.firstChild) {
        currentElement = event.target.firstChild;
      }
      console.log("id: " + currentElement.id + "  brakes: " + brakes);

      helpTextPosition.current = { left: event.pageX, top: event.pageY };
      const htUnderscoreIndex = currentElement.id.indexOf("_", 3);
      const htIdentifier =
        htUnderscoreIndex > 0
          ? currentElement.id.substring(0, htUnderscoreIndex)
          : currentElement.id;
      const helpTextObject = HELP_TEXT.find((elem) => elem.id == htIdentifier);
      if (helpTextObject) {
        helpText.current = helpTextObject.helpText;
      } else {
        helpText.current = "No description found";
      }

      if (
        currentElement.id.substring(0, 2) == "__" &&
        currentElement.id != "__helpButton"
      ) {
        setHelpTextVisible(true);
      }
    }
  };

  /**************************************************
   * Executable
   **************************************************/

  //localStorage.setItem("adminCreds", JSON.stringify(adminCredsRef.current));

  // displays local storage capacity, processes URL inline arguments
  useEffect(() => {
    if (Tools.storageAvailable("localStorage")) {
      const { size } = new Blob(Object.values(localStorage));
      console.log(
        "local storage blob size: " +
          parseInt(size / 1024) +
          "K    local storage string size: " +
          parseInt(JSON.stringify(localStorage).length / 1024) +
          "K    threshold: 5000K",
      );
    } else {
      console.log("local storage unavailable");
    }

    const queryParameters = new URLSearchParams(window.location.search);

    queryParameters.forEach((value, key) => {
      if (key == "target") {
        if (value == "Data View") {
          setProduct("oada");
        }
        if (value == "Requirements") {
          setProduct("oacc");
          setVisibleScreen("Requirements");
        }
      }
    });
  }, []);

  // another attempt at an actual refresh
  useEffect(() => {
    const storedId = sessionStorage.getItem("refreshProjectId");
    if (storedId) {
      localStorage.removeItem("refreshProjectId");
      const authString = Tools.getXmlBodyAuthString(credsRef.current);
      const urlString = Tools.getInstanceUrl(credsRef.current);
      const requestString =
        authString +
        '<Read type="Project" method="equal to" limit="1" enable_custom="1"><Project><id>' +
        storedId +
        "</id></Project></Read></request>";
      Tools.fetchPost(requestString, urlString, "Project").then(
        (projRecRaw) => {
          if (projRecRaw.xml.length > 0) {
            const projRec = Tools.xmlToArray(projRecRaw.xml);
            sessionStorage.setItem("projectObject", JSON.stringify(projRec[0]));
          }
          isDirty(!dirty);
        },
      );
    }
  }, []);

  if (!listenerAdded.current) {
    document.addEventListener("click", handleClickAnywhere);
    listenerAdded.current = true;
  }

  return (
    <Box>
      <CustomAlert
        alertText="Invalid Login.  Please try again."
        openAlert={openConfirmation}
        setOpenAlert={setOpenConfirmation}
        backgroundColor="crimson"
      />
      <Backdrop
        sx={{ color: "#fff", zIndex: 10 }} //, zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={wait}
        //onClick={handleClose}
      >
        <Grid
          container
          alignItems="center"
          justify="center"
          alignContent="center"
          sx={{
            padding: "25px",
            backgroundColor: waitMessage.current ? "#643f69" : null,
          }}
        >
          {waitMessage.current ? (
            <Grid item xs={12} sm={12} md={12} lg={12} align="center">
              <Typography variant="h4" sx={{ padding: "25px" }}>
                {waitMessage.current}
              </Typography>
            </Grid>
          ) : null}
          <Grid item xs={12} sm={12} md={12} lg={12} align="center">
            <CircularProgress color="inherit" />
          </Grid>
        </Grid>
      </Backdrop>

      <Box>
        <HelpModeContext.Provider
          value={{ mode: helpMode, setMode: setHelpModeState }}
        >
          <CredsContext.Provider
            value={{
              creds: credsRef.current,
              setCreds: setCreds,
            }}
          >
            <CollaborationCompanionToolbar
              waitHandler={handleWait}
              retrieveOADASettings={retrieveOADASettings}
              setVisibleScreen={displayVisibleScreen}
              creds={credsRef.current}
              setProduct={selectProduct}
            />

            <div style={{ width: "98vw" }}>
              <Toolbar variant="dense" />

              <LogContext.Provider
                value={{ log: log.current, add: addLog, clear: clearLog }}
              >
                {visibleScreen}
              </LogContext.Provider>
            </div>
          </CredsContext.Provider>
        </HelpModeContext.Provider>
      </Box>
      <Popover
        open={helpTextVisible}
        anchorReference="anchorPosition"
        anchorPosition={helpTextPosition.current}
        onClose={handleCloseHelpText}
        classes={{ paper: "helptext-popover" }}
      >
        <Typography className="helptext-typography">
          {helpText.current}
        </Typography>
      </Popover>
    </Box>
  );
}

export default OAAddOnMain;

OAAddOnMain.propTypes = {
  waitHandler: PropTypes.func,
  // queryParameters: PropTypes.object,
};

/*
<div style={{ width: `calc(100vw - 0px)` }}>
*/
